import moment from 'moment';

export const DATE_FORMATS = {
	DEFAULT: 'MM.DD.YYYY', // e.g., 12.31.2024
	ISO_DATE: 'YYYY-MM-DD', // e.g., 2024-12-31
	FULL_DATE: 'MMMM D, YYYY', // e.g., December 31, 2024
	FULL_DATE_TIME: 'MMMM Do YYYY [at] h:mm A', // e.g., December 31st 2024 at 5:00 PM
	FULL_DATE_TIME_ALT: 'MMM DD, YYYY, h:mm:ss A', // e.g., Dec 31, 2024, 5:00:00 PM
	SHORT_DATE: 'MMM Do YYYY', // e.g., Dec 31st 2024
	SHORT_DATE_ALT: 'MMM DD, YYYY', // e.g., Dec 31, 2024
	SHORT_DATE_TIME_ALT: 'MMM DD, YYYY, h:mm A', // e.g., Dec 31, 2024
	SHORT_DATE_AND_DAY_TIME: 'ddd, MMM DD, YYYY', // e.g., Tue, Dec 31, 2024
	SHORT_DATE_AND_FULL_TIME: 'MMM DD, YYYY [at] hh:mm', // e.g., Dec 31, 2024 at 5:00
	DOWNLOAD_DATE: 'DD-MM-YYYY', // e.g., 31-12-2024
	DAY_NAME: 'ddd', // e.g., Tue
	DAY_NAME_TIME: 'ddd h:mm A', // e.g., Tue 5:00 PM
	DAY_AND_MONTH: 'D MMMM', // e.g., 31 December
	MONTH_AND_DAY: 'MMMM D', // e.g., December 31
	DAY_AND_YEAR: 'MMM YYYY', // e.g., Dec 2024
	MONTH_AND_YEAR: 'MMMM YYYY', // e.g., December 2024
	DAY_NUMBER: 'D', // e.g., 31
	DAY_AND_DATE: 'ddd DD', // e.g., Mon 31
	DATE_WITH_FULL_MONTH_AND_YEAR: 'MMMM Do YYYY', // e.g., December 31st 2024
} as const;

export type DateFormat = (typeof DATE_FORMATS)[keyof typeof DATE_FORMATS];

export default function formatDate(date: moment.MomentInput, format: DateFormat | string = DATE_FORMATS.DEFAULT) {
	return moment(date).format(format);
}

const EASTERN_TIME_ZONE = 'America/New_York';

/** UTC timestamp for a wall-clock time on a given calendar day in US Eastern. */
const getEasternWallClockTimestamp = (
	year: number,
	month: number,
	day: number,
	hour: number,
	minute: number,
): number => {
	let utcMs = Date.UTC(year, month - 1, day, hour + 5, minute);

	const formatter = new Intl.DateTimeFormat('en-US', {
		timeZone: EASTERN_TIME_ZONE,
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
		hour: 'numeric',
		minute: 'numeric',
		hour12: false,
	});

	for (let attempt = 0; attempt < 5; attempt++) {
		const parts = formatter.formatToParts(new Date(utcMs));
		const eastern = {
			year: Number(parts.find(part => part.type === 'year')!.value),
			month: Number(parts.find(part => part.type === 'month')!.value),
			day: Number(parts.find(part => part.type === 'day')!.value),
			hour: Number(parts.find(part => part.type === 'hour')!.value),
			minute: Number(parts.find(part => part.type === 'minute')!.value),
		};

		if (
			eastern.year === year &&
			eastern.month === month &&
			eastern.day === day &&
			eastern.hour === hour &&
			eastern.minute === minute
		) {
			return utcMs;
		}

		const desiredMinutes = hour * 60 + minute;
		const actualMinutes = eastern.hour * 60 + eastern.minute;
		const dayDiff = day - eastern.day;

		utcMs += (desiredMinutes - actualMinutes + dayDiff * 24 * 60) * 60 * 1000;
	}

	return utcMs;
};

/** UTC timestamp for June 1, 2026 at 4:00 PM US Eastern (EST/EDT). */
export const JUNE_1_2026_4PM_EASTERN_MS = getEasternWallClockTimestamp(2026, 6, 1, 16, 0);
